import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { learnerService } from '../../services/learnerService';
import { MasteryStatus, ParentOverride } from '../../utils/masteryStatus';

// Deliberately under-claiming, matching the status vocabulary everywhere
// else in the Homeschool surface - no stronger claim-of-completion word may
// appear here (see PHASE_PROMPTS.md's Phase 6 copy rules).
const STATUS_OPTIONS: Array<{ value: MasteryStatus; label: string }> = [
  { value: 'not-attempted', label: 'Not started yet' },
  { value: 'emerging', label: 'Just getting started' },
  { value: 'developing', label: 'Making progress' },
  { value: 'secure', label: "Looks solid - let's recheck in two weeks" },
];

interface OverrideControlProps {
  learnerId: string;
  skillId: string;
  currentEffectiveStatus: MasteryStatus;
  existingOverride?: ParentOverride | null;
  onOverridden: () => void;
  size?: 'small' | 'medium';
}

/**
 * The parent-facing correction control (Phase 6). This is a trust feature,
 * not an escape hatch - see PHASE_PROMPTS.md's Phase 6 entry - so it's
 * deliberately a visible button next to every status claim, not buried in a
 * menu. Writes SkillMastery.parentOverride, a LearningEvidence row, and an
 * AuditLog entry (db_backend/api/skillMastery.js's override route).
 */
const OverrideControl: React.FC<OverrideControlProps> = ({
  learnerId,
  skillId,
  currentEffectiveStatus,
  existingOverride,
  onOverridden,
  size = 'small',
}) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<MasteryStatus>(existingOverride?.status || currentEffectiveStatus);
  const [note, setNote] = useState(existingOverride?.note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openDialog = () => {
    setStatus(existingOverride?.status || currentEffectiveStatus);
    setNote(existingOverride?.note || '');
    setError(null);
    setOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || '';
      await learnerService.overrideMastery(learnerId, skillId, status, note, token);
      setOpen(false);
      onOverridden();
    } catch (err) {
      console.error('Error saving parent override:', err);
      setError('Something went wrong saving your correction. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button size={size} variant="text" onClick={openDialog} sx={{ textTransform: 'none' }}>
        {existingOverride ? 'Edit your correction' : 'Doesn’t look right?'}
      </Button>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set your own status for this skill</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The system's guess is built from graded items alone - it can miss things you've seen
            firsthand. Your correction is saved as its own dated record and always takes priority
            over the computed status, everywhere it's shown.
          </Typography>
          {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="override-status-label">Status</InputLabel>
            <Select
              labelId="override-status-label"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as MasteryStatus)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Why? (optional)"
            multiline
            minRows={2}
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. She explained it to me clearly at dinner, the assessment just glitched."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save correction'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OverrideControl;
